import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import CodeBlock from "@tiptap/extension-code-block";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect, useCallback, useState, forwardRef, useImperativeHandle } from "react";
import {
  Bold, Italic, List, ListOrdered, Link as LinkIcon,
  Undo, Redo, RemoveFormatting, Code, Heading1, Heading2, Heading3,
} from "lucide-react";
import { Button } from "@/ui/base/button";
import { Separator } from "@/ui/base/separator";
import { Input } from "@/ui/base/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/ui/base/dialog";
import { cn } from "@/nucleo/utilitarios";
import { marked } from "marked";
import { htmlToMarkdown } from "@/lib/tiptapToMarkdown";

export interface DocsRichEditorRef {
  getMarkdown: () => string;
  setFromMarkdown: (md: string) => void;
}

interface DocsRichEditorProps {
  initialMarkdown: string;
  onChange?: (markdown: string) => void;
  className?: string;
}

const ToolbarButton = ({
  onClick,
  active,
  icon: Icon,
  label,
}: {
  onClick: () => void;
  active?: boolean;
  icon: React.ElementType;
  label: string;
}) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    className={cn("h-8 w-8 p-0", active && "bg-accent text-accent-foreground")}
    onClick={onClick}
    title={label}
    aria-label={label}
  >
    <Icon className="h-4 w-4" />
  </Button>
);

export const DocsRichEditor = forwardRef<DocsRichEditorRef, DocsRichEditorProps>(
  ({ initialMarkdown, onChange, className }, ref) => {
    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: { levels: [1, 2, 3] },
          codeBlock: false,
        }),
        CodeBlock,
        Link.configure({ openOnClick: false }),
        TextAlign.configure({ types: ["heading", "paragraph"] }),
        Placeholder.configure({ placeholder: "Escreva o conteúdo da documentação..." }),
      ],
      content: "",
      onUpdate: ({ editor: e }) => {
        onChange?.(htmlToMarkdown(e.getHTML()));
      },
      editorProps: {
        attributes: {
          class:
            "prose prose-sm dark:prose-invert max-w-none focus:outline-hidden min-h-[500px] px-6 py-4 " +
            "[&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-3 " +
            "[&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-2 " +
            "[&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mb-1 " +
            "[&_pre]:bg-muted [&_pre]:p-3 [&_pre]:rounded-lg [&_pre]:text-sm [&_pre]:font-mono",
        },
      },
    });

    // Load initial markdown as HTML
    useEffect(() => {
      if (editor && initialMarkdown) {
        const html = marked.parse(initialMarkdown, { async: false }) as string;
        editor.commands.setContent(html);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor]);

    useImperativeHandle(ref, () => ({
      getMarkdown: () => (editor ? htmlToMarkdown(editor.getHTML()) : ""),
      setFromMarkdown: (md: string) => {
        if (editor) {
          const html = marked.parse(md, { async: false }) as string;
          editor.commands.setContent(html);
        }
      },
    }), [editor]);

    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState("");

    const addLink = useCallback(() => {
      setLinkUrl("");
      setLinkDialogOpen(true);
    }, []);

    const confirmarLink = useCallback(() => {
      if (linkUrl && editor) {
        editor.chain().focus().setLink({ href: linkUrl }).run();
      }
      setLinkDialogOpen(false);
    }, [editor, linkUrl]);

    if (!editor) return null;

    return (
      <div className={cn("flex-1 flex flex-col overflow-hidden", className)}>
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-0.5 px-3 py-1.5 border-b border-border bg-muted/30">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            active={editor.isActive("heading", { level: 1 })}
            icon={Heading1}
            label="Título 1"
          />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            active={editor.isActive("heading", { level: 2 })}
            icon={Heading2}
            label="Título 2"
          />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            active={editor.isActive("heading", { level: 3 })}
            icon={Heading3}
            label="Título 3"
          />
          <Separator orientation="vertical" className="h-5 mx-1" />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive("bold")}
            icon={Bold}
            label="Negrito"
          />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive("italic")}
            icon={Italic}
            label="Itálico"
          />
          <Separator orientation="vertical" className="h-5 mx-1" />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive("bulletList")}
            icon={List}
            label="Lista"
          />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive("orderedList")}
            icon={ListOrdered}
            label="Lista numerada"
          />
          <Separator orientation="vertical" className="h-5 mx-1" />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            active={editor.isActive("codeBlock")}
            icon={Code}
            label="Bloco de código"
          />
          <ToolbarButton onClick={addLink} active={editor.isActive("link")} icon={LinkIcon} label="Link" />
          <Separator orientation="vertical" className="h-5 mx-1" />
          <ToolbarButton
            onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
            icon={RemoveFormatting}
            label="Limpar formatação"
          />
          <ToolbarButton onClick={() => editor.chain().focus().undo().run()} icon={Undo} label="Desfazer" />
          <ToolbarButton onClick={() => editor.chain().focus().redo().run()} icon={Redo} label="Refazer" />
        </div>

        {/* Editor */}
        <div className="flex-1 overflow-auto">
          <EditorContent editor={editor} className="h-full" />
        </div>

        <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Adicionar link</DialogTitle>
            </DialogHeader>
            <Input
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://..."
              onKeyDown={(e) => {
                if (e.key === "Enter") confirmarLink();
              }}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={confirmarLink}>Adicionar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
);

DocsRichEditor.displayName = "DocsRichEditor";
